import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Cropper, CircleStencil, RectangleStencil } from 'react-advanced-cropper';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-advanced-cropper/dist/style.css';

// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function ImageCropperDialog({ open, image, onClose, onSave, aspectRatio = null, circularCrop = true, lockAspectRatio = false }) {
    const cropperRef = useRef(null);

    // State for rotation
    const [rotation, setRotation] = useState(0);
    const prevRotationRef = useRef(0);

    // Effect to handle rotation changes
    useEffect(() => {
        if (cropperRef.current && cropperRef.current.rotateImage) {
            const delta = rotation - prevRotationRef.current;
            cropperRef.current.rotateImage(delta);
            prevRotationRef.current = rotation;
        }
    }, [rotation]);

    // State for aspect ratio
    const [currentAspectRatio, setCurrentAspectRatio] = useState(
        circularCrop ? 1 : (aspectRatio || 0)
    );
    const [originalAspectRatio, setOriginalAspectRatio] = useState(null);

    const onReady = (state) => {
        if (state.imageSize) {
            setOriginalAspectRatio(state.imageSize.width / state.imageSize.height);
        }
    };

    const onCropHelper = () => {
        if (cropperRef.current) {
            const canvas = cropperRef.current.getCanvas();
            if (canvas) {
                canvas.toBlob((blob) => {
                    onSave(blob);
                    onClose();
                }, 'image/png');
            }
        }
    };

    const handleAspectRatioChange = (event, newFormat) => {
        if (newFormat !== null) {
            setCurrentAspectRatio(newFormat);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    overflow: 'hidden',
                },
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6">Edit Image</Typography>
                    <Box>
                        <Button color="inherit" onClick={() => {
                            let defaultRatio;
                            if (circularCrop) {
                                defaultRatio = 1;
                            } else if (lockAspectRatio) {
                                defaultRatio = aspectRatio;
                            } else {
                                defaultRatio = originalAspectRatio || 0;
                            }

                            setCurrentAspectRatio(defaultRatio);
                            setRotation(0);
                            prevRotationRef.current = 0;
                        }} sx={{ mr: 1 }}>
                            Reset
                        </Button>
                        <Button color="inherit" onClick={onClose}>
                            Cancel
                        </Button>
                    </Box>
                </Box>

                {/* Cropper Area */}
                <Box sx={{
                    position: 'relative',
                    height: 400,
                    width: '100%',
                    background: '#dedede',
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Cropper
                        ref={cropperRef}
                        src={image}
                        onReady={onReady}
                        stencilComponent={circularCrop ? CircleStencil : RectangleStencil}
                        stencilProps={{
                            aspectRatio: currentAspectRatio || undefined,
                            grid: true,
                        }}
                        checkOrientation={false} // Prevent conflict with EXIF orientation
                        minZoom={0.5}
                        defaultSize={({ imageSize, visibleArea }) => ({
                            width: (visibleArea || imageSize).width * 0.9,
                            height: (visibleArea || imageSize).height * 0.9,
                        })}
                        className="react-advanced-cropper"
                        style={{ height: '100%', width: '100%' }}
                    />
                </Box>

                {/* Controls Toolbar */}
                <Box sx={{ p: 3, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Stack spacing={3}>

                        {/* Rotation Slider */}
                        {/* Rotation Buttons */}
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Rotate</Typography>
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<Iconify icon="eva:rotate-left-fill" />}
                                onClick={() => setRotation(prev => prev - 90)}
                            >
                                Left
                            </Button>
                            <Button
                                size="small"
                                color="inherit"
                                endIcon={<Iconify icon="eva:rotate-right-fill" />}
                                onClick={() => setRotation(prev => prev + 90)}
                            >
                                Right
                            </Button>
                        </Stack>

                        {/* Aspect Ratio Config */}
                        {!circularCrop && !lockAspectRatio && (
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Ratio</Typography>
                                <ToggleButtonGroup
                                    value={currentAspectRatio}
                                    exclusive
                                    onChange={handleAspectRatioChange}
                                    size="small"
                                    color="primary"
                                >
                                    <ToggleButton value={0}>Free</ToggleButton>
                                    {originalAspectRatio && (
                                        <ToggleButton value={originalAspectRatio}>Original</ToggleButton>
                                    )}
                                    <ToggleButton value={1}>1:1</ToggleButton>
                                    <ToggleButton value={16 / 9}>16:9</ToggleButton>
                                    <ToggleButton value={4 / 3}>4:3</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        )}

                        {/* Action Buttons */}
                        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 1 }}>
                            <Button
                                onClick={onCropHelper}
                                variant="contained"
                                color="primary"
                                startIcon={<Iconify icon="solar:check-circle-bold" />}
                                sx={{ px: 4 }}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Dialog>
    );
}

ImageCropperDialog.propTypes = {
    image: PropTypes.string,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    open: PropTypes.bool,
    aspectRatio: PropTypes.number,
    circularCrop: PropTypes.bool,
    lockAspectRatio: PropTypes.bool,
};
